import { inject, Injectable } from '@angular/core';
import { HxSnackbarConfig } from './snackbar-config';
import { HxSnackbarRef } from './snackbar-ref';
import { HxSnackbarService } from './snackbar-service';

/**
 * Simplified notification service wrapping HxSnackbarService.
 *
 * Provides convenient methods for common notification types (success, error, warning, info) with sensible defaults. For
 * advanced usage, inject HxSnackbarService directly.
 *
 * @example
 *   ```typescript
 *   import { Component, inject } from '@angular/core';
 *   import { HxNotificationService } from '@complexcare/harmony';
 *
 *   @Component({...})
 *   export class MyComponent {
 *     private notifications = inject(HxNotificationService);
 *
 *     saveDocument() {
 *       // ... save logic
 *       this.notifications.success('Document saved successfully');
 *     }
 *
 *     handleError(error: Error) {
 *       this.notifications.error('Failed to save: ' + error.message);
 *     }
 *   }
 *   ```;
 */
@Injectable({
  providedIn: 'root',
})
export class HxNotificationService {
  private readonly snackbar = inject(HxSnackbarService);

  /**
   * Show a success notification (positive context)
   *
   * @example
   *   ```typescript
   *   this.notifications.success('Changes saved successfully');
   *   this.notifications.success('Item deleted', { duration: 3000 });
   *   ```;
   *
   * @param message - Message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  success(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.snackbar.show({
      message,
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'positive', // ✅ LOCKED - cannot be overridden by apps
    });
  }

  /**
   * Show an error notification (negative context)
   *
   * Errors do not auto-dismiss by default (duration: 0) to ensure users see critical messages.
   *
   * @example
   *   ```typescript
   *   this.notifications.error('Failed to save document');
   *   this.notifications.error('Connection error', { duration: 5000 }); // Auto-dismiss after 5s
   *   ```;
   *
   * @param message - Error message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  error(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.snackbar.show({
      message,
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'negative', // ✅ LOCKED - cannot be overridden by apps
    });
  }

  /**
   * Show a warning notification (caution context)
   *
   * @example
   *   ```typescript
   *   this.notifications.warning('Connection unstable - changes may not sync');
   *   this.notifications.warning('Low disk space', { duration: 6000 });
   *   ```;
   *
   * @param message - Warning message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  warning(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.snackbar.show({
      message,
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'caution', // ✅ LOCKED - cannot be overridden by apps
    });
  }

  /**
   * Show an informational notification (info context)
   *
   * @example
   *   ```typescript
   *   this.notifications.info('New features available in settings');
   *   this.notifications.info('System maintenance scheduled', { hasAction: true, actionLabel: 'Details' });
   *   ```;
   *
   * @param message - Information message to display
   * @param options - Optional configuration overrides
   * @returns Reference to the snackbar instance
   */
  info(message: string, options?: Partial<HxSnackbarConfig>): HxSnackbarRef {
    return this.snackbar.show({
      message,
      hasIcon: true,
      hasClose: true,
      duration: 4000,
      ...options,
      context: 'info', // ✅ LOCKED - cannot be overridden by apps
    });
  }

  /**
   * Dismiss all active snackbars
   *
   * @example
   *   ```typescript
   *   this.notifications.dismissAll();
   *   ```;
   */
  dismissAll(): void {
    this.snackbar.dismissAll();
  }
}
